import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export type ButtonVariant =
  | 'primary'
  | 'pickup'
  | 'delivery'
  | 'danger'
  | 'ghost'
  | 'outline'
  | 'meal'
  | 'coffee'
  | 'back';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  pickup: 'btn-pickup',
  delivery: 'btn-delivery',
  danger: 'btn-danger',
  ghost: 'btn-ghost',
  outline: 'btn-outline',
  meal: 'btn bg-brand-meal text-white hover:opacity-90 focus:ring-brand-meal',
  coffee: 'btn bg-brand-coffee text-white hover:opacity-90 focus:ring-brand-coffee',
  back: 'btn bg-brand-back text-white hover:opacity-90 focus:ring-brand-back',
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'min-h-[40px] text-sm',
  md: 'min-h-touch-comfortable text-base',
  lg: 'min-h-touch-large text-lg',
  xl: 'min-h-[88px] text-xl font-bold',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      className,
      type = 'button',
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    />
  ),
);
Button.displayName = 'Button';
